import type { IAngularEvent, IRootScopeService } from 'angular';

type Listener = (event: IAngularEvent, ...args: any[]) => void;

/*
	Standalone replacement for Tools.$rootScope as an event bus (WEB-7608).

	This is a facade, not a reimplementation. While Angular is alive there must be exactly ONE
	listener registry, because converted React code and the unconverted Angular tree talk to each
	other over the same channels. So every method delegates straight to the injected $rootScope and
	returns whatever Angular returns — including the event object, by reference.

	Returning Angular's own event object is load-bearing, not incidental:
	upsales/common/components/ui/Modal.js mutates the live event (e.state / e.resumeState) after
	$emit returns, and components/hooks/useUnsavedChangesGuard.ts reads those fields off the object
	the listener received. Synthesising our own event would break the unsaved-changes guard with no
	type error and no failing test. Same reasoning for defaultPrevented, which callers use as a veto.

	Deliberate divergence from ReduxListeners (babel/store/ReduxListeners.ts), which throws when the
	rootScope is unset: this bus must NOT throw. shadcn/pages/AIWorkspace/Chat/WorkspaceChat.tsx
	already guards its subscription with `Tools?.$rootScope?.$on(...)`, i.e. that surface can run
	before or without Angular, and throwing would regress it. With no transport, `on` queues the
	subscription until setRootScope attaches it, and `broadcast`/`emit` return a minimal inert event.
	Do not "fix" this back to throwing.

	Transport access is lazy (read inside each method, never at module load) so importing this module
	is safe before Angular has bootstrapped. The rootScope is injected from upsales/app.js, next to
	the existing ReduxListeners.setRootScope call.
*/
// Shaped like an Angular event so callers that read defaultPrevented or call preventDefault() on the
// return value keep working when there is no transport. Nothing dispatches it, so it is inert.
const inertEvent = (name: string): IAngularEvent =>
	({
		name,
		defaultPrevented: false,
		preventDefault() {
			this.defaultPrevented = true;
		}
	} as IAngularEvent);

class EventBus {
	private rootScope: IRootScopeService | null = null;
	private pending: { channel: string; listener: Listener; unsubscribe?: () => void }[] = [];

	setRootScope(rootScope: IRootScopeService | null) {
		this.rootScope = rootScope;
		if (!rootScope) {
			return;
		}
		this.pending.forEach(subscription => {
			subscription.unsubscribe = rootScope.$on(subscription.channel, subscription.listener);
		});
		this.pending = [];
	}

	/*
		Subscriptions made before the rootScope is injected are queued and attached by setRootScope,
		rather than dropped. app/index.js calls root.render() before the Angular modules are even
		required, so a React effect can reach this bus first. Dropping such a listener would be silent
		— no error, no failing test — and the component would simply never receive its channel.
	*/
	on(channel: string, listener: Listener): () => void {
		if (!this.rootScope) {
			const subscription: { channel: string; listener: Listener; unsubscribe?: () => void } = {
				channel,
				listener
			};
			this.pending.push(subscription);
			return () => {
				this.pending = this.pending.filter(p => p !== subscription);
				subscription.unsubscribe?.();
			};
		}
		return this.rootScope.$on(channel, listener);
	}

	broadcast(channel: string, ...args: any[]): IAngularEvent {
		if (!this.rootScope) {
			return inertEvent(channel);
		}
		return this.rootScope.$broadcast(channel, ...args);
	}

	emit(channel: string, ...args: any[]): IAngularEvent {
		if (!this.rootScope) {
			return inertEvent(channel);
		}
		return this.rootScope.$emit(channel, ...args);
	}
}

export default new EventBus();
